import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { FileVersionData } from "@Types/fileOperations";

type FileVersionsState = Record<string, unknown[]>;
type FilesDataState = Record<string, FileVersionData>;
type FileDependenciesState = Record<string, unknown[]>;
type FilesSharedUnderResourcesState = Record<string, unknown>;
type FileVersionMergePayload = Record<string, unknown> & { fileId: string };

const fileVersionsSlice = createSlice({
  name: "fileVersions",
  initialState: {} as FileVersionsState,
  reducers: {
    setFileVersionData: (state, action: PayloadAction<{ fileId: string; data: unknown[] }>) => {
      const { fileId, data } = action.payload;
      state[fileId] = data;
    },
    mergeFileVersionData: (state, action: PayloadAction<FileVersionMergePayload>) => {
      const val = action.payload;
      const existing = (state[val.fileId] ?? []) as unknown[];
      const updated = [...existing, val];
      state[val.fileId] = updated;
    },
    setFileVersionStatus: (state) => state,
  },
});

const filesDataSlice = createSlice({
  name: "filesData",
  initialState: {} as FilesDataState,
  reducers: {
    setFilesData: (state, action: PayloadAction<Record<string, FileVersionData>>) => ({
      ...state,
      ...action.payload,
    }),
    resetFilesData: (state) => state,
    modifyFileUnitsData: (
      state,
      action: PayloadAction<{ fileVersionId: string; unitsNew: Record<string, unknown> }>,
    ) => {
      const { fileVersionId, unitsNew } = action.payload;
      const fileData = state[fileVersionId];
      if (!fileData) return state;
      const updatedUnits = { ...fileData.Units, ...unitsNew };
      return {
        ...state,
        [fileVersionId]: { ...fileData, Units: updatedUnits },
      };
    },
  },
});

const fileDependenciesSlice = createSlice({
  name: "fileDependencies",
  initialState: {} as FileDependenciesState,
  reducers: {
    setFileDependencies: (state, action: PayloadAction<{ fileVersionId: string; data: unknown[] }>) => {
      const { fileVersionId, data } = action.payload;
      state[fileVersionId] = [...data];
    },
  },
});

const filesSharedUnderResourcesSlice = createSlice({
  name: "filesSharedUnderResources",
  initialState: {} as FilesSharedUnderResourcesState,
  reducers: {
    setResourcesUnderWhichFilesShared: (_state, action: PayloadAction<{ fileId: string; data: unknown }>) => ({
      [action.payload.fileId]: action.payload.data,
    }),
  },
});

const currentFileVersionIdSlice = createSlice({
  name: "currentFileVersionId",
  initialState: "",
  reducers: {
    setFileVersionId: (_state, action: PayloadAction<string>) => action.payload,
  },
});

export const { setFileVersionData, mergeFileVersionData, setFileVersionStatus } = fileVersionsSlice.actions;

export const { setFilesData: setFilesDataAction, resetFilesData, modifyFileUnitsData } = filesDataSlice.actions;

export const { setFileDependencies: setFileDependenciesAction } = fileDependenciesSlice.actions;

export const { setResourcesUnderWhichFilesShared: setResourcesUnderWhichFilesSharedAction } =
  filesSharedUnderResourcesSlice.actions;

export const { setFileVersionId: setFileVersionIdAction } = currentFileVersionIdSlice.actions;

export const fileVersions = fileVersionsSlice.reducer;
export const setFilesData = filesDataSlice.reducer;
export const setFileDependencies = fileDependenciesSlice.reducer;
export const filesSharedUnderResources = filesSharedUnderResourcesSlice.reducer;
export const currentFileVersionId = currentFileVersionIdSlice.reducer;
